import { ageOn } from './bmr';
import { PROFILE_LIMITS, PROTEIN_PER_KG } from './config';
import { isValidIsoDate } from './dates';
import { num } from './text';
import type { Profile } from './types';

/**
 * Lo que impide calcular con este perfil, en frases para enseñar tal cual.
 * Lista vacía = se puede calcular: `buildTargets` no lanzará.
 *
 * Es la única puerta: `saveProfile` no guarda nada que tenga problemas, y un
 * perfil inválido que llegue de otro dispositivo se pide corregir en vez de
 * dejar la app en blanco.
 */
export function profileProblems(profile: Profile, today: string): string[] {
  const problems: string[] = [];
  const { ageYears, heightCm, weightKg } = PROFILE_LIMITS;

  if (!isValidIsoDate(profile.birthDate)) {
    problems.push('La fecha de nacimiento no es válida.');
  } else {
    const age = ageOn(profile.birthDate, today);
    if (age < ageYears.min) {
      problems.push(
        `NutriGym usa referencias de la EFSA para adultos: hace falta tener ${ageYears.min} años o más.`,
      );
    } else if (age > ageYears.max) {
      problems.push('Revisa la fecha de nacimiento.');
    }
  }

  if (!inRange(profile.heightCm, heightCm)) {
    problems.push(`La altura debe estar entre ${heightCm.min} y ${heightCm.max} cm.`);
  }
  if (!inRange(profile.weightKg, weightKg)) {
    problems.push(`El peso debe estar entre ${weightKg.min} y ${weightKg.max} kg.`);
  }
  if (!inRange(profile.proteinPerKg, PROTEIN_PER_KG)) {
    problems.push(
      `La proteína debe estar entre ${num(PROTEIN_PER_KG.min)} y ${num(PROTEIN_PER_KG.max)} g/kg.`,
    );
  }

  const manual = profile.manualTargets ?? {};
  if (Object.values(manual).some((v) => v != null && !(v >= 0))) {
    problems.push('Los objetivos puestos a mano no pueden ser negativos.');
  }

  return problems;
}

function inRange(value: number, { min, max }: { min: number; max: number }): boolean {
  return value >= min && value <= max;
}
